<template>
  <div
    class="browse-filter"
    :class="{ 'has-category': !!currentCategory, 'has-subcategory': !!currentSubcategory }"
  >
    <div v-if="(isSearching && currentGroup) || (currentGroup && currentCategory)" class="back">
      <a @click="back">
        <font-awesome-icon icon="chevron-left" />
        {{ backTitle }}
      </a>
    </div>
    <h5>
      {{ shopBy }}
    </h5>
    <ul v-if="!currentGroup" class="categories">
      <li
        v-for="group in groups"
        :key="group.value"
      >
        <a @click="select('group', group.value)">
          {{ group.name }}
        </a>
      </li>
    </ul>
    <ul v-if="currentGroup" class="categories">
      <li class="all" :class="{ active: !currentCategory }">
        <a @click="select('group', currentGroup.value)">
          All {{ currentGroup.name }}
        </a>
      </li>
      <li
        v-for="cat in categories"
        :key="cat.value"
        :class="{
          active: currentCategory && currentCategory.value === cat.value && !currentSubcategory
        }"
      >
        <a @click="select('category', cat.value)">
          {{ cat.name }}
        </a>
        <ul v-if="currentCategory && currentCategory.value === cat.value">
          <li
            v-for="subcat in subcategories"
            :key="subcat.value"
            :class="{ active: currentSubcategory && currentSubcategory.value === subcat.value }"
          >
            <a @click="select('subcategory', subcat.value)">
              {{ subcat.name }}
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
<script>
import { checkDependency } from '@/datalayer/helpers/attributes';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const findCurrentOption = (attributes, slug, value) => {
  const attrib = attributes.find((a) => a.slug === slug);

  if (attrib) {
    const option = attrib.type_options.options.find((o) => o.value === value);

    return option;
  }

  return null;
};

export default {
  components: {
    FontAwesomeIcon,
  },
  props: {
    isSearching: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    category: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    subcategory: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    values: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    backTitle() {
      if (this.currentCategory) {
        return `Back to all ${this.currentGroup.name}`;
      }

      if (this.isSearching && this.currentGroup) {
        return 'Back to all results';
      }

      if (this.currentGroup) {
        return `Back to all ${this.currentGroup.name}`;
      }

      return 'Back';
    },
    shopBy() {
      return this.currentGroup ? 'Shop by category:' : 'Shop by:';
    },
    name() {
      return this.currentCategory?.name || 'Category';
    },
    currentGroup() {
      const { group } = this.values;
      return findCurrentOption(this.$store.state.browse.attributes, 'group', group);
    },
    currentCategory() {
      const { category } = this.values;
      return findCurrentOption(this.$store.state.browse.attributes, 'category', category);
    },
    currentSubcategory() {
      const { subcategory } = this.values;
      return findCurrentOption(this.$store.state.browse.attributes, 'subcategory', subcategory);
    },
    groups() {
      let options = [...(this.group?.type_options?.options || [])];

      options = options.filter((o) => checkDependency(o.dependency, this.values));
      return options;
    },
    categories() {
      let options = [...(this.category?.type_options?.options || [])];

      options = options.filter((o) => checkDependency(o.dependency, this.values));
      return options;
    },
    subcategories() {
      let options = [...(this.subcategory?.type_options?.options || [])];

      options = options.filter((o) => checkDependency(o.dependency, this.values));
      return options;
    },
  },
  methods: {
    select(event, value) {
      if (event === 'category') {
        const isMobile = (window.innerWidth < 768);

        if (this.currentCategory && this.currentCategory.value === value) {
          this.$emit(isMobile ? event : 'subcategory', null);
        } else {
          this.$emit(event, value);
        }
      } else {
        this.$emit(event, value);
      }
    },
    back() {
      if (this.currentCategory) {
        return this.$emit('category', null);
      }

      return this.$emit('group', null);
    },
  },
};
</script>
<style lang="scss" scoped>
.browse-filter {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  h5 {
    font-family: Barlow, sans-serif;
    font-weight: 600;
    line-height: 40px;
    margin-bottom: 0;
  }
}

.back {
  border-bottom: 1px solid #c7ced7;
  padding-bottom: 16px;
  margin-bottom: 16px;
}

.back a {
  font-family: Barlow, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
}

.back a svg {
  margin-right: 8px;
  height: 12px;
  width: 7px;
}

.categories {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    font-family: Roboto, sans-serif;
    font-size: 16px;
    line-height: 24px;
    margin: 4px 0;

    ul {
      list-style: none;
      margin: 0 0 0 16px;
      padding: 0;
    }

    a {
      cursor: pointer;
      color: #434d59;
      font-weight: normal;
    }
  }

  .active > a,
  .active > a:hover {
    font-weight: bold;
    text-decoration: underline;
    color: #1b1b1b;
  }

  .all {
    display: none;
  }
}

@media (max-width: 767px) {
  .categories {
    .all {
      display: block;
    }
  }

  .back {
    display: none;
  }
}
</style>
